import React, { memo, useCallback, useMemo, useState } from 'react'
import ApolloErrorMessages from 'transfix-ui/components/ApolloErrorMessages/ApolloErrorMessages'
import {
  ConfirmationModal,
  IConfirmationModalProps,
} from 'transfix-ui/components/Modals/ConfirmationModal'
import TextArea from 'transfix-ui/components/TextArea'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { Typography, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { IError, IBid } from 'types/graphqlTypes'
import { useMPAnalytics } from 'apps/marketplace/hooks'
import { TEST_IDS } from './constants'
import { useSendOptionForApprovalMutation } from './graphql/SendOptionForApproval'

export interface ISendOptionForApprovalConfirmationModalProps
  extends Pick<IConfirmationModalProps, 'onClose'> {
  optionId: IBid['id']
  onSuccess: () => void
}

const useSendOptionForApprovalConfirmationModalStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  noteInput: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  optionalTag: {
    color: brandColors.coolGray5,
  },
}))

const SendOptionForApprovalConfirmationModal = memo<ISendOptionForApprovalConfirmationModalProps>(
  ({ onClose, onSuccess, optionId }) => {
    const { trackEvent } = useMPAnalytics()
    const classes = useSendOptionForApprovalConfirmationModalStyles()

    const { openToast } = useGlobalToast()

    const [mutationErrors, setMutationErrors] = useState<IError[]>([])
    const [note, setNote] = useState<string>()

    const noteChangeHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNote(e.currentTarget.value)
    }

    const [sendOptionForApproval, { error, loading }] = useSendOptionForApprovalMutation()

    const sendForApprovalHandler = useCallback(async () => {
      setMutationErrors([])
      const { data } = await sendOptionForApproval({
        variables: {
          input: {
            bidApproval: {
              bidId: optionId,
              comment: note,
            },
          },
        },
      })
      const { createBidApproval } = data || {}
      const { bidApproval, errors = [] } = createBidApproval || {}
      const { id: bidApprovalId } = bidApproval || {}

      if (errors.length) {
        trackEvent('Carrier Options', 'ERROR_REQUESTING_OPTION_APPROVAL', { bidApprovalId, errors })
        setMutationErrors(errors)
        return
      }

      if (bidApprovalId) {
        trackEvent('Carrier Options', 'REQUESTED_OPTION_APPROVAL', {
          bidApprovalId,
          bidId: optionId,
        })
        openToast({ toastMessage: 'Option sent for approval', toastType: 'default' })
        onSuccess()
      }
    }, [note, onSuccess, openToast, optionId, sendOptionForApproval, trackEvent])

    const modalActions: IConfirmationModalProps['actions'] = useMemo(
      () => [
        {
          label: 'Cancel',
          type: 'dismiss',
          disabled: loading,
          action: onClose,
        },
        {
          label: 'Send for Approval',
          type: 'confirm',
          dataTest: TEST_IDS.submitButton,
          disabled: loading,
          action: sendForApprovalHandler,
        },
      ],
      [loading, onClose, sendForApprovalHandler]
    )
    return (
      <ConfirmationModal
        dataTest={TEST_IDS.modal}
        title='Send option to lead for approval?'
        actions={modalActions}
        onClose={onClose}
        open>
        <ApolloErrorMessages mutationErrors={mutationErrors} apolloErrors={error} />
        <div className={classes.root}>
          <Typography variant='body2' component='div'>
            This option will be prioritized for the lead and you&apos;ll be notified of the
            decision.
          </Typography>
          <div className={classes.noteInput}>
            <Typography variant='subtitle2' component='div'>
              Note&nbsp;
              <span className={classes.optionalTag}>(optional)</span>
            </Typography>
            <TextArea
              placeholder='Add notes'
              size='medium'
              value={note}
              onChange={noteChangeHandler}
            />
          </div>
        </div>
      </ConfirmationModal>
    )
  }
)

export default SendOptionForApprovalConfirmationModal
